
import { themeVars } from '../../../pdc-platform/src/ts/themes/themeConstants';
import { IValuesCollection } from '../ts/appTheme';
import { ITimeIntervals, INumberIntervals } from './AqlUtility';
import { ATTRIBUTE_TYPES, TIME_PART_TYPES } from './ConfigurationConstants';

const moment = require('pdc-platform-moment');
const Globalize = require('pdc-platform-Globalize');

export interface ITimeZone {
    offsetName?: string;
    useOffset?: boolean;
    useCustom?: boolean;
    customMinutes?: number;
    customString?: string;
}

export function isDate(value) {
    return value != null && !isNaN(Date.parse(value));
}

// this f. should be removed! Instead, use next 4 functions:
export function isTypeOfDate(type: string): boolean {
    return type === ATTRIBUTE_TYPES.DATE || type === ATTRIBUTE_TYPES.DATETIME;
}

export function isTypeOfDatetime(type: string): boolean {
    return type === ATTRIBUTE_TYPES.DATETIME;
}

//LL: 23.04.24:
export function isTypeOfDatePure(type: string): boolean {
    return type === ATTRIBUTE_TYPES.DATE;
}

export function isTypeOfDateOrDatetime(type: string): boolean {
    return type === ATTRIBUTE_TYPES.DATE || type === ATTRIBUTE_TYPES.DATETIME;
}

export function isTypeOfDatetimeOrTime(type: string): boolean {
    return type === ATTRIBUTE_TYPES.DATETIME || type === ATTRIBUTE_TYPES.TIME;
}

export function isTypeOfTime(type: string): boolean {
    return type === ATTRIBUTE_TYPES.TIME;
}

export function isTypeOfAnyDate(type: string): boolean {
    return type === ATTRIBUTE_TYPES.DATE || type === ATTRIBUTE_TYPES.DATETIME || type === ATTRIBUTE_TYPES.TIME;
}

export function isTypeOfInteger(type: string) {
    return type === ATTRIBUTE_TYPES.INTEGER || type === ATTRIBUTE_TYPES.BIGINT;
}

// to check if the 'timeZoneFromTheme' is set by User:
export function timeZoneIsUsed(timeZone: ITimeZone) {
    if (timeZone != null) {
        return timeZone.useOffset || timeZone.useCustom;
    } else {
        return false;
    }
}

export function isFiscalTimePeriod(timePeriod: string) {
    return timePeriod === TIME_PART_TYPES.FISCAL_YEAR
        || timePeriod === TIME_PART_TYPES.HALF_FISCAL_YEAR
        || timePeriod === TIME_PART_TYPES.FISCAL_QUARTER;
}

export enum useDateTimeIntervalType {
    useRegular = 0,
    useUserDefined,
    useTimePeriods,
    useNone //LL: 13.04.20
}

function hasRegularIntervals(
    value: {
        timeIntervals: ITimeIntervals;
        numberIntervals: INumberIntervals;
    },
    attributeType: string
) {
    let hasRegular = false;

    if (value != null) {
        if (isTypeOfAnyDate(attributeType)) {
            if (value.timeIntervals != null && value.timeIntervals.timePart !== 'none') {
                hasRegular = true;
            }
        } else if (attributeType === 'i' || attributeType === 'r') {
            if (value.numberIntervals != null && value.numberIntervals.intervalLength != 0) {
                hasRegular = true;
            }
        }
    }

    return hasRegular;
}

function hasUserDefinedIntervals(value) {
    let hasUserDefined = false;

    if (value != null) {
        //if (value.values != null && value.values.length > 0) {
        //    hasUserDefined = true;
        //}
        //LL: 02.11.23:
        if (value.modePredefinedOrOwn === "UsePredefined") {
            hasUserDefined = value.predefinedMapping != null;
        }
        else if (value.values != null && value.values.length > 0) {
            hasUserDefined = true;
        }
    }

    return hasUserDefined;
}

function hasTimePeriodsFunc(value) {
    let hasTimePeriods = false;

    if (value != null) {
        if (value.timePeriods != null && value.timePeriods.timePeriod !== 'none') {
            hasTimePeriods = true;
        }
    }

    return hasTimePeriods;
}

export function evalUsingIntervals(value, attributeType: string) {
    let hasRegular = hasRegularIntervals(value, attributeType);
    let hasUserDefined = hasUserDefinedIntervals(value);
    let hasTimePeriods = hasTimePeriodsFunc(value);

    let useRegular = value.useIntervalType === useDateTimeIntervalType.useRegular;
    let useUserDefined = value.useIntervalType === useDateTimeIntervalType.useUserDefined;
    let useTimePeriods = value.useIntervalType === useDateTimeIntervalType.useTimePeriods;

    //LL: 13.04.20: DateTimeFunctions.useDateTimeIntervalType.useNone  ???  not needed here ?

    if (value.useIntervalType == null) {
        if (hasRegular) {
            useRegular = true;
        } else if (hasUserDefined) {
            useUserDefined = true;
        } else if (hasTimePeriods) {
            useTimePeriods = true;
        }
    }

    return {
        hasRegular: hasRegular,
        hasUserDefined: hasUserDefined,
        hasTimePeriods: hasTimePeriods,
        useRegular: useRegular,
        useUserDefined: useUserDefined,
        useTimePeriods: useTimePeriods
    };
}

// for Elena: accept my 'userOffset' object and 'timeStamp' like: 1403465838805;
// returns the OffSet in minutes, like -480.
export function getOffsetInMinutes(timeZone: ITimeZone, timeStamp) {
    if (timeZone == null) {
        return null;
    }

    if (timeZone.useOffset) {
        //LL: 03.10.23: problem with TZ like "Etc/GMT-4"  ===========================
        // see https://momentjs.com/timezone/docs/#/zone-object/ :
        // Offset  zone.utcOffset(timestamp); // 480
        //   Get the offset for a given timestamp (in milliseconds) from a Zone.
        //  POSIX compatibility requires that the offsets are inverted.
        //  Therefore, Etc/GMT-X will have an offset of +X and Etc/GMT+X will have an offset of -X.
        //  This is a result of IANA's Time Zone Database and not an arbitrary choice by Moment.js.
        //  Thus, using locality based identifiers is preferred over fixed-offset identifiers.
        //
        //  This is also described on the Wikipedia entry for the database:
        //    The special area of "Etc" is used for some administrative zones,
        //    particularly for "Etc/UTC" which represents Coordinated Universal Time.
        //    In order to conform with the POSIX style,
        //    those zone names beginning with "Etc/GMT" have their sign reversed from the standard ISO 8601 convention.
        //    In the "Etc" area, zones west of GMT have a positive sign and
        //    those east have a negative sign in their name (e.g "Etc/GMT-14" is 14 hours ahead of GMT).
        //
        // For example, using the Europe/Madrid identifier gives a different result from Etc/GMT+1.
        //    moment().tz('Etc/GMT+1').format('YYYY-MM-DD HH:mm ZZ'); // '2014-12-18 11:22 -0100'
        //    moment().tz('Europe/Madrid').format('YYYY-MM-DD HH:mm ZZ'); // '2014-12-18 13:22 +0100'

        //  try:
        //let n = timeZone.offsetName;
        //let z = moment.tz.zone(timeZone.offsetName);
        //let u = moment.tz.zone(timeZone.offsetName).utcOffset(timeStamp);

        //LL: 05.10.23: NOTE:
        // We discussed the problem in our group and decided that this change below must be removed,
        // because moment.tz follows the IANA rules and we must not correct them.
        // Also we decided to add some explanation in our UtcOffsetField.tsx about this matter
        //LL: 03.10.23:
        //let offset = moment.tz.zone(timeZone.offsetName).utcOffset(timeStamp);
        //if (timeZone.offsetName.startsWith('Etc/GMT')) {
        //    offset = -offset;
        //}
        //return offset;

        //LL: was
        return moment.tz.zone(timeZone.offsetName).utcOffset(timeStamp);
    }

    if (timeZone.useCustom) {
        return timeZone.customMinutes;
    }

    return null; // used 'Browser default', i.e. NOT SET.
}

// accept my 'userOffset' object and 'DateTime' string like: "2015-01-01T09:00:00+03:00";
// returns the OffSet in string, like '+03:00'.
export function getOffsetInString(timeZone: ITimeZone, strDateTime) {
    if (timeZone == null) {
        //return null;
        const timeZoneName = moment.tz.guess();
        return moment.tz(strDateTime, timeZoneName).format('Z');
    }

    if (timeZone.useOffset) {
        return moment.tz(strDateTime, timeZone.offsetName).format('Z');
    }

    if (timeZone.useCustom) {
        return timeZone.customString;
    }

    //return null; // used 'Browser default', i.e. NOT SET.
    return moment.tz(strDateTime, moment.tz.guess()).format('Z');
}

export function getTimeZoneName(timeZone: ITimeZone): string {
    if (timeZone == null) {
        return moment.tz.guess();
    }

    if (timeZone.useOffset) {
        return timeZone.offsetName;
    }

    if (timeZone.useCustom) {
        return timeZone.customString;
    }

    return moment.tz.guess();
    //'TODO2'; // used 'Browser default', i.e. NOT SET.
}

//LL: please, don't make better this function
export function evaluateDateWithOffset(value, timeZone: ITimeZone): Date {
    if (value == null) {
        //EVG 26.08.21: here was -> return;
        //It means that if value is null the function returns undefined.
        //Please, don't do it. The function is used in many places.
        //It must return either Date or the value.
        //If you do need it returns undefined then write it explictly, please.
        //Like -> return undefined;
        //It will allow others to see that changing null to undefined is designed.
        return value;
    }

    const dateValue = Date.parse(value);

    let d = new Date();
    d.setTime(dateValue);

    //LL:  _dateOffset for each date separately
    //  Wrong to do it once for new Date(), i.e. Today(), because of Summer time (Daylight vs Standard TZ)
    //  Today() may be of different season - winter/summer

    let dateOffset = 0; // in millisec

    if (timeZoneIsUsed(timeZone)) {
        //TimezoneOffset is the difference between UTC and local time
        const tzOffsetLocal = -d.getTimezoneOffset() * 60 * 1000; // getTimezoneOffset is in minutes

        const reqOffsetMinutes = getOffsetInMinutes(timeZone, dateValue);
        //LL:  VR returns -120 when offset is +02:00
        const reqOffset = reqOffsetMinutes * 60 * 1000;
        dateOffset = tzOffsetLocal + reqOffset; // in millisec
    }

    if (dateOffset !== 0) {
        d = new Date();
        d.setTime(dateValue - dateOffset);
    }

    return d;
}

export function formatDateInUTC(
    date: Date | string
) {
    let dateMOut: Moment = moment(date); //VR

    if (typeof date == 'string') {
        console.debug('formatDateInUTC(): Input is "String"=', date);
        //dateOut = new Date(date);
    }
    else if (typeof date == 'object') {
        console.debug('formatDateInUTC(): Input is "Object"=', date);
        //dateOut = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }
    else {
        console.debug('_common\\platform\\ts\\DateTimeFunctions.formatDateInUTC: ==ERROR==> unknown type of input param: "date" ');
        return date;
    }
    //console.debug('Output date =', dateOut);

    // const dateOut = new Date();
    // dateOut.setTime(Date.parse(date as string)); ???????

    // VR: 8 Nov 2022: it is correct
    // const nDay = dateOut.getDate();
    // const sDay = nDay < 10 ? '0' + nDay : nDay.toString();
    // const nMonth = dateOut.getMonth() + 1;
    // const sMonth = nMonth < 10 ? '0' + nMonth : nMonth.toString();
    //console.debug('formatDateInUTC(): Output string=', dateOut.getFullYear() + '-' + sMonth + '-' + sDay);

    // return dateOut.getFullYear() + '-' + sMonth + '-' + sDay;


    console.debug('formatDateInUTC(): Output string=', dateMOut.format("YYYY-MM-DD"), 'Timezone= ', dateMOut.format('TZ'));
    return dateMOut.format("YYYY-MM-DD");
}

export function formatDateForBackendByType(
    date: Date | string,
    attributeType: string | 'date' | 'datetime' | 'time',
    timeZone: ITimeZone
) {
    if (date == null || date === '') {
        return null;
    }

    if (attributeType === 'date') {
        return formatDateInUTC(date);
    }

    let loc;

    if (typeof date === 'string') {
        if (date.indexOf('Z') === date.length - 1) {
            return date;
        }

        //Example: "2018-07-09T12:01:00+02:00"
        const findSemicolon = date.split(':');

        if (findSemicolon != null && findSemicolon.length === 4) {
            return date;
        }

        loc = moment.tz(date, 'UTC');
    } else {
        loc = moment(date);
    }

    if (attributeType == 'datetime') {
        return loc.format('YYYY-MM-DDTHH:mm:ss.SSS') + getOffsetInString(timeZone, loc.format('YYYY-MM-DDTHH:mm:ss'));
    }

    return loc.format('YYYY-MM-DDTHH:mm:ss') + getOffsetInString(timeZone, loc.format('YYYY-MM-DDTHH:mm:ss'));
}

//EVG 22.02.2022: This function is here because it has some the same code as formatDateForBackendByType() above.
//Both should be reviewed.
export function formatDateForIntervalSelect(
    date: Date | string,
    attributeType: string | 'date' | 'datetime' | 'time',
    timeZone: ITimeZone
): string {
    if (date == null || date === '') {
        return null;
    }

    if (attributeType === 'date') {
        return formatDateInUTC(date);
    }

    let loc;

    if (typeof date === 'string') {
        const findSemicolon = date.split(':'); //VR: ex: "2018-07-09T12:01:00+02:00"

        if (findSemicolon != null && findSemicolon.length === 4 && date.indexOf('+00:00') < 0) {
            //VR: Well, this VALUE already has been here - pass it AS IS!
            return date;
        }

        loc = moment.tz(date, 'UTC');
    } else {
        loc = moment(date);
    }

    if (attributeType == 'datetime') {
        return loc.format('YYYY-MM-DDTHH:mm:ss.SSS') + getOffsetInString(timeZone, loc.format('YYYY-MM-DDTHH:mm:ss'));
    }

    return loc.format('YYYY-MM-DDTHH:mm:ss') + getOffsetInString(timeZone, loc.format('YYYY-MM-DDTHH:mm:ss'));
}

export function getTimeZoneFromTheme(theme: IValuesCollection): ITimeZone {
    return theme[themeVars.utcOffsetSelector];
}

// ======== for "time" in Charts, IntervalSelect

//LL: 24.09.18:
export const TimeValueFormat = 'yyyy-MM-ddTHH:mm:ss'; //LL: 29.08.18
export const TimeDisplayFormat = 'HH:mm:ss'; //LL: 29.08.18
export const TimeDisplayFormatShort = 'HH:mm';

const stdCulture = 'en'; //LL: 25.03.20:   time format is hh:mm:ss

//LL: 05.10.18: month is Jan, Date use 0 . Date is with TZ, so day = 2, not 1, so that no negative values
export const TimeDefaultDate = { year: 1970, month: 1, day: 2 };

export function getDefaultDate() {
    return new Date(TimeDefaultDate.year, TimeDefaultDate.month, TimeDefaultDate.day);
}

//LL: from 'yyyy-MM-ddTHH:mm:ss' to 'HH:mm:ss'
export function convertTimeValueAsDateToString(dateString: string): string {
    let timeString = dateString;
    let D = Globalize(stdCulture).parseDate(timeString, {
        raw: TimeValueFormat
    });
    
    if (D != null) {
        timeString = Globalize(stdCulture).formatDate(D, {
            raw: TimeDisplayFormat
        });
    }
    
    return timeString;
}

//LL: from 'yyyy-MM-ddTHH:mm:ss' to 'HH:mm'
export function convertTimeValueAsDateToString_Short(dateString: string): string {
    let timeString = dateString;
    let D = Globalize(stdCulture).parseDate(timeString, {
        raw: TimeValueFormat
    });
    
    if (D != null) {
        timeString = Globalize(stdCulture).formatDate(D, {
            raw: TimeDisplayFormatShort
        });
    }
    
    return timeString;
}

//LL: timeString is usually "hh:mm:ss" (TimeDisplayFormat)
export function convertTimeStringToDateString(timeString: string) {
    let D = Globalize(stdCulture).parseDate(timeString, {
        raw: TimeValueFormat
    });
    
    if (D != null) {
        return timeString;
    }

    
    let tStr = timeString;
    
    D = Globalize(stdCulture).parseDate(tStr, { raw: TimeDisplayFormat });
    
    if (D == null) {
        D = Globalize(stdCulture).parseDate(tStr, {
            raw: TimeDisplayFormatShort
        });
        if (D != null) {
            tStr += ':00';
        }
    }
    
    if (D != null) {
        //LL: D is with TZ, so day = 2, not 1, so that no negative values
        //D.setFullYear(1970, 0, 1);
        //  without TZ:  ???
        return '1970-01-02T' + tStr; // use const TimeDefaultDate
    }

    return timeString;
}

//LL: timeString is usually "hh:mm:ss" (TimeDisplayFormat)
export function convertTimeStringToDate(timeString: string) {
    let D = Globalize(stdCulture).parseDate(timeString, {
        raw: TimeValueFormat
    });
    
    if (D != null) {
        return D;
    }

    //LL: try
    //let dateValue = Date.parse(timeString); // NaN
    //console.log(dateValue);

    let tStr = timeString;
    
    D = Globalize(stdCulture).parseDate(tStr, { raw: TimeDisplayFormat });
    
    if (D == null) {
        D = Globalize(stdCulture).parseDate(tStr, {
            raw: TimeDisplayFormatShort
        });
        
        if (D != null) {
            tStr += ':00';
        }
    }

    //LL:  timeString was "20:20:00.0000000"     (7 zeros)
    if (D == null) {
        let m = timeString.indexOf('.');
        
        if (m >= 0) {
            tStr = timeString.substr(0, m);
            
            D = Globalize(stdCulture).parseDate(tStr, {
                raw: TimeDisplayFormat
            });
            
            if (D == null) {
                D = Globalize(stdCulture).parseDate(tStr, {
                    raw: TimeDisplayFormatShort
                });
                if (D != null) {
                    tStr += ':00';
                }
            }
        }
    }

    if (D != null) {
        //LL: D is with TZ, so day = 2, not 1, so that no negative values
        //D.setFullYear(1970, 0, 1);
        //  without TZ:  ???
        let dStr = '1970-01-02T' + tStr; // use const TimeDefaultDate

        D = Globalize(stdCulture).parseDate(dStr, { raw: TimeValueFormat }); //LL: will be with TZ (MSK +03)
    }

    return D;
}

//LL: 13.05.20: from 'yyyy-MM-ddTHH:mm:ss' to 'yyyy-MM-ddTHH:mm:00'
export function nullifySeconds(timeString: string): string {
    let D: any = Globalize(stdCulture).parseDate(timeString, {
        raw: TimeValueFormat
    });

    if (D == null) {
        return timeString;
    }

    D.setSeconds(0);

    return Globalize(stdCulture).formatDate(D, { raw: TimeValueFormat });
}

export function TwoDigits(s: number): string {
    if (s < 10) {
        return '0' + s;
    } else {
        return '' + s;
    }
}
